import { Currency } from "@redotech/money/currencies";
import {
  LineItem,
  ServiceInvoiceType,
} from "@redotech/redo-billing-common/service-invoice";
import { isDef } from "@redotech/util/checks";
import { getDateString } from "@redotech/util/date-format";
import { assertNever } from "@redotech/util/type";
import { memo, ReactNode, useMemo } from "react";
import { RedoBaseModal } from "../arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "../arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableLayout,
} from "../arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "../arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "../arbiter-components/tables/redo-table-header-cells";
import { Flex } from "../flex";

export interface ServiceInvoiceTableRow {
  _id: string;
  type: ServiceInvoiceType;
  lineItems: LineItem[];
  paid: boolean;
  dateOfLastBillingAttempt: Date | null | undefined;
  billingDueDate: Date;
  refunded?: boolean;
}

export const ServiceInvoiceModal = memo(function ServiceInvoiceModal({
  activeItem,
  closeModal,
  additionalContent,
}: {
  activeItem: ServiceInvoiceTableRow | undefined;
  closeModal: () => void;
  additionalContent?: (item: ServiceInvoiceTableRow) => ReactNode;
}) {
  const lineItems = useMemo(() => {
    const totalPrice =
      activeItem?.lineItems.reduce((acc, item) => acc + (item.price ?? 0), 0) ||
      0;
    const totalQuantity =
      activeItem?.lineItems.reduce(
        (acc, item) => acc + (item.quantity ?? 0),
        0,
      ) || 0;
    const totalPriceRoundedToNearestCent = Math.round(totalPrice * 100) / 100;

    const finalLineItem: LineItem = {
      name: "Total",
      price: totalPriceRoundedToNearestCent,
      quantity: totalQuantity,
      currency: Currency.USD,
      typeHelper: "total",
    };

    return [...(activeItem?.lineItems || []), finalLineItem];
  }, [activeItem]);

  const modalSubtitle = useMemo(() => {
    if (!activeItem) {
      return "";
    }

    if (activeItem.paid) {
      return `Paid ${getDateString(activeItem.billingDueDate)}`;
    } else {
      return `Due ${getDateString(activeItem.billingDueDate)}`;
    }
  }, [activeItem]);

  const quantityColumnHeader = (() => {
    const type = activeItem?.type;
    switch (type) {
      case ServiceInvoiceType.MARKETING_SMS:
      case ServiceInvoiceType.MARKETING_EMAIL:
      case ServiceInvoiceType.SUPPORT_BASE:
      case ServiceInvoiceType.SUPPORT_AI_SUBSCRIPTION:
      case ServiceInvoiceType.SUPPORT_AI_USAGE:
        return "Messages";
      case ServiceInvoiceType.CHECKOUT_OPTIMIZATION:
        return "Orders with Redo checkout experience";
      case ServiceInvoiceType.SUPPORT_VOICE_SUBSCRIPTION:
      case ServiceInvoiceType.SUPPORT_VOICE_USAGE:
        return "Minutes";
      case ServiceInvoiceType.RETURN_AI_EXCHANGE:
      case ServiceInvoiceType.RETURNS:
        return "Returns";
      case ServiceInvoiceType.AEO:
        return "AEO";
      case ServiceInvoiceType.IMS:
        return "IMS";
      case ServiceInvoiceType.IMS_FORECASTING:
        return "IMS Forecasting";
      case ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS:
        return "Events";
      case undefined:
      case ServiceInvoiceType.ORDER_TRACKING:
      case ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION:
      case ServiceInvoiceType.RECOVER:
      case ServiceInvoiceType.DISPUTES:
      case ServiceInvoiceType.LCG_COMMIT:
      case ServiceInvoiceType.CTX_USAGE:
        return "Quantity";
      default:
        assertNever(type);
    }
  })();

  const lineItemTableLayout: RedoTableLayout<LineItem> = {
    headerColor: "grey",
    columns: [
      {
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Type" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            leading={row.typeHelper === "total"}
            subtext={row.nameDescription}
            text={row.name}
          />
        ),
      },
      {
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText={quantityColumnHeader} />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            subtext={row.quantityDescription}
            text={isDef(row.quantity) ? row.quantity.toLocaleString() : "-"}
          />
        ),
      },
      {
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Cost" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            leading={row.typeHelper === "total"}
            text={row.price ? `$${row.price.toLocaleString()}` : "-"}
          />
        ),
      },
    ],
  };

  return (
    <RedoBaseModal isOpen={!!activeItem} onModalCloseRequested={closeModal}>
      <RedoModalHeader
        cancelClicked={closeModal}
        subtitle={modalSubtitle}
        title="Itemized invoice"
      />
      <Flex dir="column" gap="md">
        <RedoTable layout={lineItemTableLayout} rowsOfData={lineItems} />
        {activeItem && additionalContent?.(activeItem)}
      </Flex>
      <RedoModalFooter
        buttonPlacement="tight"
        secondaryButton={{ text: "Cancel", onClick: closeModal }}
      />
    </RedoBaseModal>
  );
});
